/**
 * Marketplace API — car listings, search, favorites
 */
import apiClient from './client';

export interface ListingFilters {
    make_id?: string;
    model_id?: string;
    year_min?: number;
    year_max?: number;
    price_min?: number;
    price_max?: number;
    transmission?: string;
    fuel_type?: string;
    country_code?: string;
    city?: string;
    search?: string;
    page?: number;
}

export const marketplaceApi = {
    list: async (filters?: ListingFilters) => {
        const { data } = await apiClient.get('/marketplace/listings/', { params: filters });
        return data;
    },

    get: async (id: number | string) => {
        const { data } = await apiClient.get(`/marketplace/listings/${id}/`);
        return data;
    },

    create: async (payload: object) => {
        const { data } = await apiClient.post('/marketplace/listings/', payload);
        return data;
    },

    update: async (id: number | string, payload: object) => {
        const { data } = await apiClient.patch(`/marketplace/listings/${id}/`, payload);
        return data;
    },

    delete: async (id: number | string) => {
        await apiClient.delete(`/marketplace/listings/${id}/`);
    },

    favorite: async (id: number | string) => {
        const { data } = await apiClient.post(`/marketplace/listings/${id}/favorite/`);
        return data;
    },

    // Admin actions
    approve: async (id: number | string) => {
        const { data } = await apiClient.post(`/marketplace/listings/${id}/approve/`);
        return data;
    },

    reject: async (id: number | string, reason: string) => {
        const { data } = await apiClient.post(`/marketplace/listings/${id}/reject/`, { reason });
        return data;
    },
};

/**
 * Parts / catalog API — products, categories, cart, orders
 */
export const partsApi = {
    // Categories
    getCategories: async () => {
        const { data } = await apiClient.get('/catalog/categories/');
        return data;
    },

    // Products
    getProducts: async (params?: object) => {
        const { data } = await apiClient.get('/catalog/products/', { params });
        return data;
    },

    getProduct: async (id: number | string) => {
        const { data } = await apiClient.get(`/catalog/products/${id}/`);
        return data;
    },

    createProduct: async (payload: object) => {
        const { data } = await apiClient.post('/catalog/products/', payload);
        return data;
    },

    updateProduct: async (id: number | string, payload: object) => {
        const { data } = await apiClient.patch(`/catalog/products/${id}/`, payload);
        return data;
    },

    // Cart
    getCart: async () => {
        const { data } = await apiClient.get('/sales/cart/');
        return data;
    },

    addToCart: async (variantId: number, quantity: number = 1) => {
        const { data } = await apiClient.post('/sales/cart/add/', { variant_id: variantId, quantity });
        return data;
    },

    removeFromCart: async (lineId: number) => {
        const { data } = await apiClient.delete(`/sales/cart/remove/${lineId}/`);
        return data;
    },

    clearCart: async () => {
        const { data } = await apiClient.delete('/sales/cart/clear/');
        return data;
    },

    // Checkout
    checkout: async (payload: object) => {
        const { data } = await apiClient.post('/sales/cart/checkout/', payload);
        return data;
    },

    validateCoupon: async (code: string) => {
        const { data } = await apiClient.post('/sales/coupons/validate/', { code });
        return data;
    },

    // Stock
    getStockQuants: async (params?: object) => {
        const { data } = await apiClient.get('/inventory/quants/', { params });
        return data;
    },

    adjustStock: async (variantId: number, quantity: number, note?: string) => {
        const { data } = await apiClient.post('/inventory/quants/adjust/', {
            variant_id: variantId,
            quantity,
            note,
        });
        return data;
    },

    // POS sale (vendor only)
    posSale: async (payload: object) => {
        const { data } = await apiClient.post('/sales/orders/pos-sale/', payload);
        return data;
    },
};
